/**
 * SuppressionListPage.tsx — Suppression List (Do Not Contact).
 *
 * Every address Outrena will refuse to send to, from BOTH gate layers that
 * SequenceService.schedule_send() enforces:
 *
 *   Layer 1 "prospect" — Prospect.suppressed / consent_status = 'withdrawn'
 *   Layer 2 "email"    — address present in the EmailSuppression table
 *
 * Showing them together is the point: a hard bounce suppresses the Prospect
 * row but never writes an EmailSuppression row, while a one-click unsubscribe
 * writes EmailSuppression but may leave the Prospect flag unset. Either one
 * blocks a send, so either one has to be visible here or the page would
 * under-report why a prospect is being skipped.
 *
 * RBAC: MANAGER+ (route-gated and backend-gated). The list is tenant-wide, so
 * it necessarily shows addresses belonging to other reps' prospects.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  Building2,
  // Download,
  Loader2,
  Mail,
  MailWarning,
  MailX,
  Plus,
  Search,
  ShieldOff,
  Trash2,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

import { http } from "@/services/apiClient";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";

/* ─── Types (aligned with SuppressionListResponse) ───────────────────────── */

interface SuppressionEntry {
  id: string;
  email: string;
  source: string;
  layer: string;
  reason: string | null;
  notes: string | null;
  suppressedAt: string | null;
  prospectId: string | null;
  prospectName: string | null;
  company: string | null;
  isOptOut: boolean;
}

interface SuppressionListResponse {
  items: SuppressionEntry[];
  total: number;
  counts: Record<string, number>;
  emailSuppressionAvailable: boolean;
}

interface SourceMeta {
  id: string;
  label: string;
  icon: ReactNode;
  badgeClass: string;
  description: string;
}

/** Proper Case labels via an explicit map (casing standard — no CSS capitalize). */
const SOURCE_META: SourceMeta[] = [
  {
    id: "unsubscribed",
    label: "Unsubscribed",
    icon: <MailX className="h-3.5 w-3.5" />,
    badgeClass: "bg-red-100 text-red-700 hover:bg-red-100",
    description: "Clicked the one-click unsubscribe link in an email.",
  },
  {
    id: "consent_withdrawn",
    label: "Consent Withdrawn",
    icon: <UserX className="h-3.5 w-3.5" />,
    badgeClass: "bg-rose-100 text-rose-700 hover:bg-rose-100",
    description: "GDPR consent withdrawn, objection, or restriction.",
  },
  {
    id: "bounce",
    label: "Hard Bounce",
    icon: <MailWarning className="h-3.5 w-3.5" />,
    badgeClass: "bg-amber-100 text-amber-700 hover:bg-amber-100",
    description: "The address rejected delivery and was suppressed automatically.",
  },
  {
    id: "message_blocked",
    label: "Message Blocked",
    icon: <Ban className="h-3.5 w-3.5" />,
    badgeClass: "bg-orange-100 text-orange-700 hover:bg-orange-100",
    description: "The receiving gateway blocked the message (554).",
  },
  {
    id: "manual",
    label: "Manual",
    icon: <ShieldOff className="h-3.5 w-3.5" />,
    badgeClass: "bg-slate-100 text-slate-700 hover:bg-slate-100",
    description: "Added by a manager on this page.",
  },
  {
    id: "other",
    label: "Other",
    icon: <Ban className="h-3.5 w-3.5" />,
    badgeClass: "bg-muted text-muted-foreground hover:bg-muted",
    description: "Suppressed for a reason Outrena does not recognise.",
  },
];

function sourceMeta(id: string): SourceMeta {
  return SOURCE_META.find((s) => s.id === id) ?? SOURCE_META[SOURCE_META.length - 1];
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PAGE_SIZE_DEFAULT = 50;

export function SuppressionListPage() {
  const qc = useQueryClient();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addNotes, setAddNotes] = useState("");

  const [removeTarget, setRemoveTarget] = useState<SuppressionEntry | null>(null);
  const [removeReason, setRemoveReason] = useState("");

  // Debounce the search box so a keystroke does not fire a query each time.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, refetch, isFetching } =
    useQuery<SuppressionListResponse>({
      queryKey: ["suppression", search, source, page, pageSize],
      queryFn: () =>
        http.get<SuppressionListResponse>("/api/v1/suppression", {
          search: search || undefined,
          source: source === "all" ? undefined : source,
          limit: pageSize,
          offset: page * pageSize,
        }),
    });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // Memoised so the grandTotal useMemo below has a stable dependency — a bare
  // `data?.counts ?? {}` allocates a new object on every render.
  const counts = useMemo<Record<string, number>>(
    () => data?.counts ?? {},
    [data],
  );
  const grandTotal = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts],
  );

  const addMutation = useMutation({
    mutationFn: () =>
      http.post<{ created: boolean; message: string }>("/api/v1/suppression", {
        email: addEmail.trim().toLowerCase(),
        notes: addNotes.trim() || null,
      }),
    onSuccess: (res) => {
      if (res.created) toast.success(res.message);
      else toast.info(res.message);
      qc.invalidateQueries({ queryKey: ["suppression"] });
      setAddOpen(false);
      setAddEmail("");
      setAddNotes("");
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(detail ?? "Failed to add address");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (entry: SuppressionEntry) =>
      http.delete<{ message: string }>(
        `/api/v1/suppression/${encodeURIComponent(entry.id)}` +
          (removeReason.trim()
            ? `?reason=${encodeURIComponent(removeReason.trim())}`
            : ""),
      ),
    onSuccess: (res) => {
      toast.success(res.message ?? "Suppression removed");
      qc.invalidateQueries({ queryKey: ["suppression"] });
      setRemoveTarget(null);
      setRemoveReason("");
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(detail ?? "Failed to remove suppression");
    },
  });

  /** Export the CURRENT filter, not just the page, by re-querying with a high limit. */
  // const [exporting, setExporting] = useState(false);
  // const handleExportCsv = async () => {
  //   setExporting(true);
  //   try {
  //     const all = await http.get<SuppressionListResponse>("/api/v1/suppression", {
  //       search: search || undefined,
  //       source: source === "all" ? undefined : source,
  //       limit: 1000,
  //       offset: 0,
  //     });
  //     const headers = [
  //       "email",
  //       "source",
  //       "layer",
  //       "prospectName",
  //       "company",
  //       "suppressedAt",
  //       "reason",
  //       "notes",
  //     ];
  //     const esc = (v: unknown) => {
  //       const s = String(v ?? "");
  //       return s.includes(",") || s.includes('"') || s.includes("\n")
  //         ? `"${s.replace(/"/g, '""')}"`
  //         : s;
  //     };
  //     const csv = [
  //       headers.join(","),
  //       ...all.items.map((r) =>
  //         headers
  //           .map((h) => esc((r as unknown as Record<string, unknown>)[h]))
  //           .join(","),
  //       ),
  //     ].join("\n");
  //     const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = `suppression-list-${new Date().toISOString().slice(0, 10)}.csv`;
  //     a.click();
  //     URL.revokeObjectURL(url);
  //     toast.success(`Exported ${all.items.length} address${all.items.length !== 1 ? "es" : ""}`);
  //   } catch {
  //     toast.error("Export failed");
  //   } finally {
  //     setExporting(false);
  //   }
  // };

  const filterChips = [
    { id: "all", label: "All", count: grandTotal },
    ...SOURCE_META.map((s) => ({
      id: s.id,
      label: s.label,
      count: counts[s.id] ?? 0,
    })),
  ].filter((c) => c.id === "all" || c.count > 0 || source === c.id);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppression List"
        description="Every address Outrena will not send to — unsubscribes, bounces, blocks and manual entries, from both suppression layers."
        actions={
          <div className="flex items-center gap-2">
            {/* <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={exporting || total === 0}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export CSV
            </Button> */}
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Address
            </Button>
          </div>
        }
      />

      {data && !data.emailSuppressionAvailable && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">
                Email-level suppression table is missing
              </p>
              <p className="text-amber-800">
                This tenant has no <code>EmailSuppression</code> table, so only
                prospect-level suppressions are listed and addresses blocked by
                the email-level gate are not shown. Run{" "}
                <code>alembic upgrade head</code> (migration 0021) to restore the
                full list.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 pt-6">
          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {filterChips.map((chip) => (
                <Button
                  key={chip.id}
                  variant={source === chip.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSource(chip.id);
                    setPage(0);
                  }}
                >
                  {chip.label}
                  <Badge
                    variant="secondary"
                    className="ml-2 bg-background/80 px-1.5 text-xs text-foreground"
                  >
                    {chip.count}
                  </Badge>
                </Button>
              ))}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search email or notes…"
                className="pl-9"
              />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <ErrorState
              title="Could not load the suppression list"
              onRetry={() => void refetch()}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<ShieldOff className="h-10 w-10" />}
              title={
                search || source !== "all"
                  ? "No addresses match this filter"
                  : "No suppressed addresses"
              }
              description={
                search || source !== "all"
                  ? "Clear the search or pick a different source."
                  : "Nobody has unsubscribed, bounced or been blocked yet. Addresses appear here automatically."
              }
            />
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Prospect</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Suppressed</TableHead>
                      <TableHead className="w-[70px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((entry) => {
                      const meta = sourceMeta(entry.source);
                      return (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="break-all">{entry.email}</span>
                            </div>
                            {entry.notes && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {entry.notes}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={`gap-1 font-normal ${meta.badgeClass}`}
                              title={meta.description}
                            >
                              {meta.icon}
                              {meta.label}
                            </Badge>
                            {/* Which gate holds this address — makes the two
                                layers drifting apart visible. */}
                            <p className="mt-1 text-xs text-muted-foreground">
                              {entry.layer === "email"
                                ? "Email-level"
                                : "Prospect-level"}
                            </p>
                          </TableCell>
                          <TableCell className="text-sm">
                            {entry.prospectName ?? (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {entry.company ? (
                              <span className="flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                {entry.company}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(entry.suppressedAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRemoveTarget(entry);
                                setRemoveReason("");
                              }}
                              title="Remove from suppression list"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(0);
                  }}
                />
              </div>
              {isFetching && (
                <p className="text-xs text-muted-foreground">Refreshing…</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add address dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Address To Suppression List</DialogTitle>
            <DialogDescription>
              Blocks this address across every campaign immediately. Use it when
              somebody asks to be removed by replying rather than using the
              unsubscribe link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sup-email">Email Address</Label>
              <Input
                id="sup-email"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="person@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sup-notes">Notes (optional)</Label>
              <Textarea
                id="sup-notes"
                value={addNotes}
                onChange={(e) => setAddNotes(e.target.value)}
                placeholder="e.g. Asked to be removed by reply on 6 Oct"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={
                addMutation.isPending ||
                !addEmail.trim() ||
                !addEmail.includes("@")
              }
            >
              {addMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add To List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation — deliberately harsher copy for an opt-out. */}
      <AlertDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoveTarget(null);
            setRemoveReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removeTarget?.isOptOut
                ? "Remove an opt-out record?"
                : "Remove from suppression list?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.isOptOut ? (
                <>
                  <strong className="text-destructive">
                    {removeTarget.email} asked not to be contacted
                  </strong>{" "}
                  ({sourceMeta(removeTarget.source).label.toLowerCase()}).
                  Removing this record makes the address sendable again, which
                  means they can receive outreach after opting out. This is
                  recorded in the audit log against your account. Only continue
                  if you know the opt-out was made in error.
                </>
              ) : (
                <>
                  {removeTarget?.email} will become sendable again across all
                  campaigns. Appropriate when a typo has been corrected or a
                  mailbox has been restored. This action is recorded in the
                  audit log.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="remove-reason">Reason (optional, audited)</Label>
            <Input
              id="remove-reason"
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
              placeholder="e.g. Address was a typo — mailbox now valid"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (removeTarget) removeMutation.mutate(removeTarget);
              }}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {removeTarget?.isOptOut ? "Remove Opt-Out" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SuppressionListPage;
